import currency from 'currency.js'
import { date } from 'quasar'
import { isAdmin, isConsumer } from 'src/config'
import ActivityTicket from 'src/models/activity-ticket/ActivityTicket'
import BookingFeeType, { TYPES as BOOKING_FEE_TYPES } from 'src/models/booking-fee-type/BookingFeeType'
import { DELIVERY_TYPE_OPTIONS } from 'src/models/delivery-type/DeliveryType'
import Discount, { DISCOUNT_AMOUNT_TYPES } from 'src/models/discount/Discount'
import GiftCard from 'src/models/gift-card/GiftCard'
import { SALES_ITEM_RESTRICTION_TYPES } from 'src/models/inventory-strategy/InventoryStrategy'
import { fastPrepMinutes } from 'src/models/location/Location'
import MembershipInterval from 'src/models/membership/MembershipInterval'
import Model, { encoders, ID, Integer, IsoDateTime, JsonArray, JsonObject } from 'src/models/model'
import Order, { getAncestorOrder } from 'src/models/order/Order'
import ItemOrderItem from 'src/models/order-item/ItemOrderItem'
import ItemPackage from 'src/models/package/ItemPackage'
import RegisterStation from 'src/models/register/RegisterStation'
import Showing from 'src/models/showing/Showing'
import Ticket from 'src/models/ticket/Ticket'
import User from 'src/models/user/User'
import UserPoint from 'src/models/user-point/UserPoint'
import IssuedVoucherPackage from 'src/models/voucher/IssuedVoucherPackage'
import Voucher from 'src/models/voucher/Voucher'
import VoucherType from 'src/models/voucher/VoucherType'
import bookingFeePriceComputer from 'src/services/booking-fees/BookingFeePriceComputer'
import itemBookingFeeComputer from 'src/services/booking-fees/BookingFeesItemComputer'
import ticketBookingFeeComputer from 'src/services/booking-fees/BookingFeesTicketComputer'
import { isOutOfStock } from 'src/services/inventory/sales-restrictions'
import orderItemDeliveryOptionsComputer from 'src/services/item-delivery/OrderItemDeliveryOptionsComputer'
import priceCalculator from 'src/services/pricing/PriceCalculator'

const { addToDate } = date

export const ORDER_ITEM_STATES = {
  DRAFT: 'draft',
  PLACED: 'placed',
  PAID: 'paid',
  VOIDED: 'voided'
}
ORDER_ITEM_STATES.ALL = _.values(ORDER_ITEM_STATES)
ORDER_ITEM_STATES.NOT_VOIDED_STATES = _.without(ORDER_ITEM_STATES.ALL, ORDER_ITEM_STATES.VOIDED)

export const ORDER_ITEM_TYPES = {
  ITEM: 'item',
  TICKET: 'ticket',
  ACTIVITY_TICKET: 'activity-ticket',
  MEMBERSHIP_INTERVAL: 'membership-interval',
  DAY_PASS: 'day-pass',
  VOUCHER_PURCHASE: 'voucher-purchase',
  VOUCHER_PACKAGE_PURCHASE: 'voucher-package-purchase',
  VOUCHER_SERVICE_ISSUE: 'voucher-service-issue',
  GIFT_CARD: 'gift-card',
  MISC_ITEM: 'misc-item',
  ACCOUNTING_ADJUSTMENT: 'accounting-adjustment',
  DONATION: 'donation',
  USER_CREDIT: 'user-credit',
  ADJUST_POINTS: 'adjust-points',
  ADJUST_CREDIT: 'adjust-credit',
  TIP: 'tip',
  BOOKING_FEE: 'booking-fee',
  EXCHANGE_FEE: 'exchange-fee',
  SURCHARGE: 'surcharge'
}
ORDER_ITEM_TYPES.ALL = _.values(ORDER_ITEM_TYPES)
ORDER_ITEM_TYPES.VIRTUAL = _.without(ORDER_ITEM_TYPES.ALL, ORDER_ITEM_TYPES.ITEM)
ORDER_ITEM_TYPES.REQUIRES_EMAIL = [ORDER_ITEM_TYPES.VOUCHER_PURCHASE, ORDER_ITEM_TYPES.GIFT_CARD, ORDER_ITEM_TYPES.VOUCHER_PACKAGE_PURCHASE]
ORDER_ITEM_TYPES.SCHEDULED_LIVE_EVENT = [ORDER_ITEM_TYPES.TICKET, ORDER_ITEM_TYPES.ACTIVITY_TICKET]
ORDER_ITEM_TYPES.SET_AMOUNT = [ORDER_ITEM_TYPES.MISC_ITEM, ORDER_ITEM_TYPES.ACCOUNTING_ADJUSTMENT, ORDER_ITEM_TYPES.DONATION, ORDER_ITEM_TYPES.TIP, ORDER_ITEM_TYPES.USER_CREDIT, ORDER_ITEM_TYPES.SURCHARGE]
ORDER_ITEM_TYPES.BOOKING_FEE_CONDITION = [ORDER_ITEM_TYPES.TICKET, ORDER_ITEM_TYPES.ITEM]
ORDER_ITEM_TYPES.ALLOWED_IN_CONSUMER_TAB = [ORDER_ITEM_TYPES.ITEM, ORDER_ITEM_TYPES.TIP, ORDER_ITEM_TYPES.BOOKING_FEE, ORDER_ITEM_TYPES.DONATION, ORDER_ITEM_TYPES.SURCHARGE]
ORDER_ITEM_TYPES.ALLOWED_IN_MEMBERSHIP_ORDER = [ORDER_ITEM_TYPES.TIP, ORDER_ITEM_TYPES.SURCHARGE, ORDER_ITEM_TYPES.MEMBERSHIP_INTERVAL]
ORDER_ITEM_TYPES.CONSUMER_REFUNDABLE = [ORDER_ITEM_TYPES.TICKET, ORDER_ITEM_TYPES.BOOKING_FEE]

export const ORDER_ITEM_SUBTYPES = {
  MEMBERSHIP_INTERVAL_REFUND: 'refund',
  MEMBERSHIP_INTERVAL_PRORATE: 'prorated-charge',
  MEMBERSHIP_INTERVAL_NEW: 'new',
  MEMBERSHIP_INTERVAL_VOID: 'void',
  GIFT_CARD_RECHARGE: 'recharge'
}
export default class OrderItem extends Model {
  static name = 'OrderItem'

  static versioned = true

  static employeeRequiredPermissionCategories = [Model.permissionCategories.Customer]

  beforeRemove () {
    this.state === ORDER_ITEM_STATES.DRAFT && this.orderItemBookingFee && this.order.orderItems.remove(this.orderItemBookingFee)
  }

  afterAttach () {
    // Initialize a new, unsaved OrderItem's custom price to the default variation price,
    // if custom prices are allowed, but let employees edit the amount afterward.
    if (this.type === ORDER_ITEM_TYPES.ITEM && this.hasCustomPrice && !_.isNumber(this.data.amount)) {
      // The timeout is temporary fix for cases where running this callback, even after a nextTick,
      // sometimes causes an infinite loop.
      setTimeout(() => {
        this.data.amount = this.itemOrderItem?.price || 0
        this.data.price = this.data.amount
      }, 10)
    }
  }

  static define () {
    this.fields = {
      id: {
        type: ID,
        required: true
      },
      order: {
        type: Order,
        reference: true,
        output: true,
        computed () {
          return getAncestorOrder(this) || this.data?.order
        }
      },
      type: {
        type: String,
        options: ORDER_ITEM_TYPES.ALL
      },
      subtype: {
        type: String,
        options: ['membership-interval-refund', 'membership-interval-prorated-charge', 'membership-interval-new']
      },
      state: {
        type: String,
        output: true,
        default: ORDER_ITEM_STATES.DRAFT,
        options: ORDER_ITEM_STATES.ALL
      },

      description: {
        type: String,
        default: null,
        translatable: true
      },
      details: {
        type: JsonArray,
        default () {
          return []
        }
      },

      userPoints: {
        type: [UserPoint],
        reference: true,
        output: true,
        default: () => ([]),
        excludeFromDefault: true
      },

      print: {
        type: Boolean,
        default: false,
        local: true
      },

      itemOrderItem: {
        type: ItemOrderItem,
        default: null,
        encode: encoders.if(function () { return this.type === ORDER_ITEM_TYPES.ITEM })
      },
      ticket: {
        type: Ticket,
        default: null,
        encode: encoders.if(function () { return this.type === ORDER_ITEM_TYPES.TICKET })
      },
      activityTicket: {
        type: ActivityTicket,
        default: null,
        encode: encoders.if(function () { return this.type === ORDER_ITEM_TYPES.ACTIVITY_TICKET })
      },
      activityBookingId: {
        type: ID,
        sortable: true
      },
      membershipInterval: {
        type: MembershipInterval,
        default: null
      },
      voucherPurchase: {
        type: Voucher,
        default: null,
        encode: encoders.if(function () { return this.type === ORDER_ITEM_TYPES.VOUCHER_PURCHASE || this.type === ORDER_ITEM_TYPES.VOUCHER_SERVICE_ISSUE })
      },
      giftCard: {
        type: GiftCard,
        default: null,
        encode: encoders.if(function () { return this.type === ORDER_ITEM_TYPES.GIFT_CARD })
      },
      issuedVoucherPackage: {
        type: IssuedVoucherPackage,
        default: null,
        encode: encoders.if(function () { return this.type === ORDER_ITEM_TYPES.VOUCHER_PACKAGE_PURCHASE })
      },
      localBookingFeeType: {
        type: BookingFeeType,
        default: null,
        local: true
      },
      bookingFeeType: {
        type: BookingFeeType,
        default: null,
        encode: encoders.hasOne,
        reference: true
      },
      pointsUsed: {
        type: Integer,
        default: 0
      },
      pointsEarned: {
        type: Integer,
        default: 0
      },
      discount: {
        type: Discount,
        encode: encoders.hasOne,
        default: null,
        reference: true
      },
      discountUserId: {
        type: ID,
        default: null
      },
      manualDiscount: {
        type: Boolean,
        default: false
      },
      discountGroupKey: {
        type: Integer,
        default: null
      },
      forceApplyDiscountId: {
        type: ID,
        default: null
      },
      skipVoucher: {
        type: Boolean,
        default: false
      },
      voucher: {
        type: Voucher,
        encode: encoders.hasOne,
        default: null,
        reference: true
      },
      localVoucher: {
        type: Voucher,
        default: null,
        local: true
      },
      voucherType: {
        type: VoucherType,
        encode: encoders.hasOne,
        default: null,
        reference: true
      },
      voucherTypeSwapped: {
        type: Boolean,
        default: false
      },
      voucherUserId: {
        type: ID,
        default: null
      },
      recognizedVoucherRevenue: {
        type: Number,
        output: true
      },
      itemPackage: {
        type: ItemPackage,
        encode: encoders.hasOne,
        default: null,
        reference: true
      },
      showing: {
        type: Showing,
        encode: encoders.hasOne,
        reference: true
      },

      amount: {
        type: Number,
        encode: encoders.cents,
        computed () {
          if (!this.data) return

          if (this.paid && _.includes(ORDER_ITEM_TYPES.SET_AMOUNT, this.type)) return this.data.price

          return this.data.amount
        }
      },
      orderItemBookingFee: {
        type: OrderItem,
        default: null,
        output: true
      },
      price: {
        type: Number,
        output: true,
        computed () {
          if (!this.data) return

          if (this.paid) return this.data.price

          // Membership prices are computed on the server
          if (this.type === ORDER_ITEM_TYPES.MEMBERSHIP_INTERVAL) return this.data.price

          // Exchange fee prices are computed on the server
          if (this.type === ORDER_ITEM_TYPES.EXCHANGE_FEE) return this.data.price

          let price = this.priceSetByCinema
          if (this.setAsGross) {
            _.each(this.taxes, tax => {
              price = currency(price).subtract(tax.taxFromGross(price, this.totalTaxPercentThousandths)).value
            })
          }

          return price
        }
      },
      discountAmount: {
        type: Number,
        output: true,
        computed () {
          if (this.paid) return this.data.discountAmount

          let discountAmount = this.displayDiscountAmount

          if (this.setAsGross) {
            _.each(this.taxes, tax => {
              discountAmount = currency(discountAmount).subtract(tax.taxFromGross(discountAmount, this.totalTaxPercentThousandths)).value
            })
          }

          return discountAmount
        }
      },
      netAmount: {
        type: Number,
        output: true,
        computed () {
          if (this.paid) return this.data.netAmount

          return this.subtotal
        }
      },
      voucherAmount: {
        type: Number,
        output: true,
        computed () {
          if (this.paid) return this.data.voucherAmount

          let voucherAmount = this.displayVoucherAmount

          if (voucherAmount) {
            if (this.setAsGross) {
              _.each(this.taxes, tax => {
                voucherAmount = currency(voucherAmount).subtract(tax.taxFromGross(voucherAmount, this.totalTaxPercentThousandths)).value
              })
            }

            return voucherAmount
          } else {
            return 0
          }
        }
      },
      taxable: {
        type: Boolean,
        default: false,
        computed () {
          if (!this.type) return

          if (this.paid) return this.data?.taxable

          if (this.type === ORDER_ITEM_TYPES.MISC_ITEM || this.type === ORDER_ITEM_TYPES.ACCOUNTING_ADJUSTMENT) return this.data?.taxable

          if (this.type === ORDER_ITEM_TYPES.ITEM) {
            return this.itemOrderItem?.item?.taxable
          } else if (this.type === ORDER_ITEM_TYPES.TICKET || this.type === ORDER_ITEM_TYPES.ACTIVITY_TICKET) {
            return this.configuration.currentSite.ticketsTaxable
          } else if (this.type === ORDER_ITEM_TYPES.MEMBERSHIP_INTERVAL) {
            return this.configuration.currentCircuit.membershipTaxable
          } else if (this.type === ORDER_ITEM_TYPES.DAY_PASS) {
            return this.configuration.currentSite.dayPassTaxable
          } else if (this.type === ORDER_ITEM_TYPES.VOUCHER_PURCHASE) {
            return false
          } else if (this.type === ORDER_ITEM_TYPES.VOUCHER_PACKAGE_PURCHASE) {
            return false
          } else if (this.type === ORDER_ITEM_TYPES.VOUCHER_SERVICE_ISSUE) {
            return false
          } else if (this.type === ORDER_ITEM_TYPES.GIFT_CARD) {
            return false
          } else if (this.type === ORDER_ITEM_TYPES.DONATION) {
            return false
          } else if (this.type === ORDER_ITEM_TYPES.TIP) {
            return false
          } else if (this.type === ORDER_ITEM_TYPES.BOOKING_FEE) {
            if (this.paid) return this.data?.taxable
            return this.bookingFeeType?.taxable || this.data?.taxable
          } else if (this.type === ORDER_ITEM_TYPES.EXCHANGE_FEE) {
            return this.configuration.currentSite.exchangeFeeTaxEnabled
          } else if (this.type === ORDER_ITEM_TYPES.SURCHARGE) {
            return this.configuration.currentSite.surchargeTaxEnabled
          } else {
            throw new Error(`Unhandled order item type: ${this.type}`)
          }
        }
      },
      simpleOrderItemTaxesById: {
        type: JsonObject,
        output: true,
        computed () {
          if (this.paid ||
            this.type === ORDER_ITEM_TYPES.MEMBERSHIP_INTERVAL ||
            (this.isRemote && (this.type === ORDER_ITEM_TYPES.MISC_ITEM || this.type === ORDER_ITEM_TYPES.ACCOUNTING_ADJUSTMENT))) {
            return this.data.simpleOrderItemTaxesById
          }

          return this.pricing.taxes
        }
      },
      taxIds: {
        type: [ID],
        default: () => [],
        input: true
      },
      taxAmount: {
        type: Number,
        output: true,
        computed () {
          if (this.paid) {
            return this.data.taxAmount
          }
          return currency(this.pricing.taxAmount).add(this.bookingFeeTaxAmount).value
        }
      },

      splitPaymentDetails: {
        type: JsonObject,
        default: () => { return { splitRatio: 0, payAmount: 0, paidAmount: 0 } }
      },
      note: {
        type: String
      },

      deliveryType: {
        type: String,
        default: null
      },
      hasDueTime: {
        type: Boolean,
        default: null
      },
      dueTime: {
        type: IsoDateTime,
        default: null,
        encode (data) {
          if (this.relativeDueTimeMinutes) {
            data.dueTime = new IsoDateTime(addToDate(new Date(), { minutes: this.relativeDueTimeMinutes }))
          } else {
            data.dueTime = this.data.dueTime
          }
        }
      },
      relativeDueTimeMinutes: {
        type: Number,
        default: null,
        local: true
      },

      pickPrepPrintingStatus: {
        type: String,
        output: true
        // excludeFromDefault: true
      },

      nameForOrder: {
        type: String,
        default: null
      },
      pagerNumber: {
        type: String,
        default: null
      },
      deliveryDetails: {
        type: String,
        default: null
      },
      deliveryLocation: {
        type: String,
        default: null
      },
      deliveryPositions: {
        type: JsonArray,
        default: []
      },
      tableName: {
        type: String,
        default: null
      },
      theaterSeatName: {
        type: String,
        default: null
      },
      pickPrepPrintedAt: {
        type: IsoDateTime,
        default: null,
        excludeFromDefault: true
      },
      validations: {
        type: JsonObject,
        default: {},
        output: true
      },

      canVoid: {
        type: Boolean,
        output: true,
        excludeFromDefault: true
      },
      cannotVoidReason: {
        type: String,
        output: true,
        excludeFromDefault: true
      },

      paidUser: {
        type: User,
        output: true,
        default: null,
        reference: true
      },

      collectedUser: {
        type: User,
        output: true,
        default: null,
        reference: true
      },
      collectedRegisterStation: {
        type: RegisterStation,
        output: true,
        default: null
      },
      collectedAt: {
        type: IsoDateTime,
        output: true
      },
      preparedAt: {
        type: IsoDateTime,
        output: true
      },
      deliveredAt: {
        type: IsoDateTime,
        output: true
      },
      itemSourceLocationId: {
        type: ID,
        default: null
      },
      computedPaidSubtotal: {
        type: Number,
        default: 0,
        output: true
      },
      computedPaidTotal: {
        type: Number,
        default: 0,
        output: true
      },
      createdAt: {
        type: IsoDateTime,
        output: true
      },
      placedAt: {
        type: IsoDateTime
      },
      paidAt: {
        type: IsoDateTime
      },
      effectiveAt: {
        type: IsoDateTime,
        output: true
      },
      voidedAt: {
        type: IsoDateTime
      },
      isCashOut: {
        type: Boolean,
        default: false
      },
      group: {
        type: Boolean,
        local: true,
        default: true
      },

      transient: {
        type: Boolean,
        default: false,
        local: true
      },

      packingRequired: {
        type: Boolean,
        output: true,
        excludeFromDefault: true
      },

      isSwappable: {
        type: Boolean,
        default: false,
        output: true
      },

      explicitDiscountRemovalIds: {
        type: [Number],
        local: true,
        default () {
          return []
        }
      },
      itemPackageDetails: {
        type: JsonObject,
        default: () => ({
          excludedItemPackageIds: [],
          packageGroupIdentifier: null
        }),
        mergeDefaults: true
      },
      pendingCardAssignment: {
        type: Boolean,
        default: false,
        local: true
      }
    }

    this.computed = {
      descriptionWithBreaks () {
        return this.item?.description?.replace(/\n/g, '<br>')
      },
      operative () {
        return this.draft || this.placed
      },
      draft () {
        return this.state === ORDER_ITEM_STATES.DRAFT
      },
      placed () {
        return this.state === ORDER_ITEM_STATES.PLACED || this.state === ORDER_ITEM_STATES.PAID
      },
      paid () {
        return this.state === ORDER_ITEM_STATES.PAID || this.state === ORDER_ITEM_STATES.VOIDED
      },
      // a.k.a. needs payment
      unpaid () {
        return this.state === ORDER_ITEM_STATES.DRAFT || this.state === ORDER_ITEM_STATES.PLACED
      },
      voided () {
        return this.state === ORDER_ITEM_STATES.VOIDED
      },
      // TODO OOS INDY-6757 Update availability logic -- minSelectionCount doesn't apply if the Modifier Set isn't available on some channels?
      requiredItemModifierSets () {
        if (!this.item) return []

        const requiredModifierSets = _.filter(this.item.itemModifierSets.data, itemModifierSet => itemModifierSet.modifierSet.availableOnline && itemModifierSet.minSelectionCount > 0)

        return requiredModifierSets
      },
      optionalItemModifierSets () {
        if (!this.item) return []

        const optionalModifierSets = _.filter(this.item.itemModifierSets.data, itemModifierSet => itemModifierSet.modifierSet.availableOnline && (_.isNil(itemModifierSet.minSelectionCount) || itemModifierSet.minSelectionCount === 0))

        return optionalModifierSets
      },
      selectedRequiredItemModifierSets () {
        if (!this.item) return []

        const countBySetId = _.countBy(this.itemOrderItem?.itemModifiers?.data, 'modifierSetId')

        return _.filter(this.item.itemModifierSets.data, itemModifierSet => itemModifierSet.modifierSet.availableOnline && itemModifierSet.minSelectionCount > 0 && countBySetId[itemModifierSet.modifierSet.id] >= itemModifierSet.minSelectionCount)
      },
      roundPrices () {
        if (this.type === ORDER_ITEM_TYPES.SURCHARGE || this.type === ORDER_ITEM_TYPES.TIP) {
          // Surcharge is not rounded as the price is set by the payment provider.
          // The price cannot be determined until payment is completed.
          return false
        } else if (this.type === ORDER_ITEM_TYPES.MEMBERSHIP_INTERVAL && isAdmin) {
          // Admin Membership Interval prices are set by the API to sum to an Order Total of $0.00.
          // Rounding prices can disrupt this total, blocking placing the Admin Membership Modification.
          return false
        } else {
          return this.configuration.currentSite.roundPrices
        }
      },
      priceSetByCinema () {
        // The initial retail price set for this item, before discounts or voucher, and could be net or gross
        if (this.type === ORDER_ITEM_TYPES.ITEM) {
          if (this.hasCustomPrice) {
            return _.isNumber(this.data.amount) ? this.data.amount : (this.itemOrderItem?.price || 0)
          }
          return this.itemOrderItem?.price || 0
        } else if (this.type === ORDER_ITEM_TYPES.TICKET) {
          return this.ticket?.price || 0
        } else if (this.type === ORDER_ITEM_TYPES.ACTIVITY_TICKET) {
          return this.activityTicket?.price || 0
        } else if (this.type === ORDER_ITEM_TYPES.MEMBERSHIP_INTERVAL && this.subtype === ORDER_ITEM_SUBTYPES.MEMBERSHIP_INTERVAL_REFUND) {
          return -this.membershipInterval?.amount
        } else if (this.type === ORDER_ITEM_TYPES.MEMBERSHIP_INTERVAL && this.subtype === ORDER_ITEM_SUBTYPES.MEMBERSHIP_INTERVAL_PRORATE) {
          return this.membershipInterval?.amount
        } else if (this.type === ORDER_ITEM_TYPES.MEMBERSHIP_INTERVAL && this.subtype === ORDER_ITEM_SUBTYPES.MEMBERSHIP_INTERVAL_NEW) {
          return this.membershipInterval?.amount
        } else if (this.type === ORDER_ITEM_TYPES.MEMBERSHIP_INTERVAL && this.subtype === ORDER_ITEM_SUBTYPES.MEMBERSHIP_INTERVAL_VOID) {
          return 0
        } else if (this.type === ORDER_ITEM_TYPES.VOUCHER_PURCHASE) {
          return this.voucherPurchase?.voucherType?.price || 0
        } else if (this.type === ORDER_ITEM_TYPES.VOUCHER_PACKAGE_PURCHASE) {
          return (this.issuedVoucherPackage?.voucherPackage?.price || 0)
        } else if (this.type === ORDER_ITEM_TYPES.VOUCHER_SERVICE_ISSUE) {
          return 0
        } else if (this.type === ORDER_ITEM_TYPES.DAY_PASS) {
          return this.configuration.currentSite.dayPassPrice
        } else if (this.type === ORDER_ITEM_TYPES.GIFT_CARD && this.subtype !== ORDER_ITEM_SUBTYPES.GIFT_CARD_RECHARGE) {
          return this.giftCard?.initialValue || 0
        } else if (this.type === ORDER_ITEM_TYPES.GIFT_CARD && this.subtype === ORDER_ITEM_SUBTYPES.GIFT_CARD_RECHARGE) {
          return this.amount
        } else if (this.type === ORDER_ITEM_TYPES.MISC_ITEM || this.type === ORDER_ITEM_TYPES.ACCOUNTING_ADJUSTMENT) {
          return this.amount
        } else if (this.type === ORDER_ITEM_TYPES.DONATION) {
          return this.amount
        } else if (this.type === ORDER_ITEM_TYPES.USER_CREDIT) {
          return this.amount
        } else if (this.type === ORDER_ITEM_TYPES.TIP) {
          return this.amount
        } else if (this.type === ORDER_ITEM_TYPES.BOOKING_FEE) {
          return bookingFeePriceComputer.computeForBookingFeeOrderItem(this)
        } else if (this.type === ORDER_ITEM_TYPES.EXCHANGE_FEE) {
          return this.amount
        } else if (this.type === ORDER_ITEM_TYPES.SURCHARGE) {
          return this.amount
        } else if (this.type === ORDER_ITEM_TYPES.ADJUST_CREDIT) {
          return this.amount
        } else if (!this.type) {
          return 0
        } else {
          throw new Error('Unhandled order item type: ' + this.type)
        }
      },
      hasCustomPrice () {
        return this.item?.hasCustomPrice || false
      },
      appliedVoucher () {
        return this.localVoucher || this.voucher
      },
      displayVoucherName () {
        return this.voucher?.voucherType?.name || this.voucherType?.name || ''
      },
      displayDiscountName () {
        return this.discount?.displayName || ''
      },
      redemptionTotal () {
        return currency(this.discountAmount).add(this.voucherAmount).value
      },
      displayRedemptionTotal () {
        return currency(this.displayDiscountAmount).add(this.displayVoucherAmount).value
      },
      displayDiscountAmount () {
        if (this.discount?.id) {
          // Apply rules of discount
          if (this.discount.amountType === DISCOUNT_AMOUNT_TYPES.PERCENT) {
            // return Math.round(Math.round(100 * (this.priceSetByCinema - this.displayVoucherAmount)) * this.discount.percent / 100 + Number.EPSILON) / 100

            // -0.5 round away from zero in ruby, so we need to do the same here
            const absAmount = currency(Math.abs(this.priceSetByCinema)).subtract(Math.abs(this.displayVoucherAmount))
            const absDiscountAmountValue = absAmount.multiply(this.discount.percentThousandths).divide(100000).value

            return this.priceSetByCinema > 0 ? absDiscountAmountValue : -absDiscountAmountValue
          } else if (this.discount.amountType === DISCOUNT_AMOUNT_TYPES.FIXED_AMOUNT) {
            return Math.min(currency(this.priceSetByCinema).subtract(this.displayVoucherAmount).value, this.discount.amount)
          } else if (this.discount.amountType === DISCOUNT_AMOUNT_TYPES.TARGET_AMOUNT) {
            const discountAmountRequired = currency(this.priceSetByCinema).subtract(this.displayVoucherAmount).subtract(this.discount.amount).value
            return discountAmountRequired > 0 ? discountAmountRequired : 0
          } else {
            throw new Error('Unhandled discount amount type: ' + this.discount.amountType)
          }
        } else {
          return 0
        }
      },
      minimumPrice () {
        const voucherType = this.voucherType || this.voucher?.voucherType

        return voucherType?.minPrice || 0
      },
      displayVoucherAmount () {
        const voucherType = this.voucherType || this.voucher?.voucherType

        if (voucherType) {
          const maxValueForPercent = voucherType.hasMaxPercent ? currency(this.priceSetByCinema).multiply(voucherType.maxPercentThousandths).divide(100000).value : null
          return Math.min(..._.filter([
            maxValueForPercent,
            voucherType.hasMinPrice ? Math.max(currency(this.priceSetByCinema).subtract(voucherType.minPrice).value, 0) : null,
            voucherType.hasMaxPrice ? voucherType.maxPrice : null,
            this.priceSetByCinema
          ], val => val !== null && val !== undefined))
        }

        return 0
      },
      taxes () {
        if (!this.type) return []

        if (this.paid ||
          this.type === ORDER_ITEM_TYPES.MEMBERSHIP_INTERVAL ||
          (this.isRemote && (this.type === ORDER_ITEM_TYPES.MISC_ITEM || this.type === ORDER_ITEM_TYPES.ACCOUNTING_ADJUSTMENT))) {
          return _.filter(this.configuration.taxes.data, tax => _.includes(_.keys(this.simpleOrderItemTaxesById), String(tax.id)))
        }

        if (this.type === ORDER_ITEM_TYPES.MISC_ITEM || this.type === ORDER_ITEM_TYPES.ACCOUNTING_ADJUSTMENT) return _.filter(this.configuration.taxes.data, tax => _.includes(this.taxIds, tax.id))

        if (this.type === ORDER_ITEM_TYPES.ITEM) {
          return _.filter(this.itemOrderItem?.item?.taxes?.data, tax => _.includes(tax.data.siteIds, this.configuration.currentSite.id)) || []
        } else if (_.includes(ORDER_ITEM_TYPES.SCHEDULED_LIVE_EVENT, this.type)) {
          return this.configuration.currentSite.ticketTaxes.data
        } else if (this.type === ORDER_ITEM_TYPES.MEMBERSHIP_INTERVAL) {
          return this.configuration.currentCircuit.membershipTaxes.data
        } else if (this.type === ORDER_ITEM_TYPES.DAY_PASS) {
          return this.configuration.currentSite.dayPassTaxes.data
        } else if (this.type === ORDER_ITEM_TYPES.VOUCHER_PURCHASE) {
          // Taxes are not charged
          return []
        } else if (this.type === ORDER_ITEM_TYPES.VOUCHER_PACKAGE_PURCHASE) {
          // Taxes are not charged
          return []
        } else if (this.type === ORDER_ITEM_TYPES.VOUCHER_SERVICE_ISSUE) {
          // Taxes are not charged
          return []
        } else if (this.type === ORDER_ITEM_TYPES.GIFT_CARD) {
          // Taxes are not charged
          return []
        } else if (this.type === ORDER_ITEM_TYPES.DONATION) {
          // Taxes are not charged
          return []
        } else if (this.type === ORDER_ITEM_TYPES.TIP) {
          // Taxes are not charged
          return []
        } else if (this.type === ORDER_ITEM_TYPES.BOOKING_FEE) {
          return _.filter(this.bookingFeeType?.taxes?.data, tax => _.includes(tax.data.siteIds, this.configuration.currentSite.id)) || []
        } else if (this.type === ORDER_ITEM_TYPES.EXCHANGE_FEE) {
          return this.configuration.currentSite.exchangeFeeTaxes.data
        } else if (this.type === ORDER_ITEM_TYPES.SURCHARGE) {
          return this.configuration.currentSite.surchargeFeeTaxes.data
        } else {
          throw new Error(`Unhandled order item type: ${this.type}`)
        }
      },
      totalTaxPercentThousandths () {
        if (this.paid) return _.sumBy(_.values(this.simpleOrderItemTaxesById), 'tax_percent_thousandths')

        return _.sumBy(this.taxes, 'percentThousandths')
      },
      setAsGross () {
        return this.taxable && this.taxes?.length &&
              ((this.type === ORDER_ITEM_TYPES.ITEM && this.configuration.currentSite.concessionPricesIncludeTax) ||
               ((this.type === ORDER_ITEM_TYPES.MISC_ITEM || this.type === ORDER_ITEM_TYPES.ACCOUNTING_ADJUSTMENT) && this.configuration.currentSite.concessionPricesIncludeTax) ||
               ((this.type === ORDER_ITEM_TYPES.TICKET || this.type === ORDER_ITEM_TYPES.ACTIVITY_TICKET) && this.configuration.currentSite.ticketPricesIncludeTax) ||
               (this.type === ORDER_ITEM_TYPES.BOOKING_FEE && this.bookingFeeType.type === BOOKING_FEE_TYPES.ITEM && this.configuration.currentSite.concessionPricesIncludeTax) ||
               (this.type === ORDER_ITEM_TYPES.BOOKING_FEE && this.bookingFeeType.type !== BOOKING_FEE_TYPES.ITEM && this.configuration.currentSite.ticketPricesIncludeTax) ||
               (this.type === ORDER_ITEM_TYPES.MEMBERSHIP_INTERVAL && this.configuration.currentCircuit.membershipPricesIncludeTax) ||
               (this.type === ORDER_ITEM_TYPES.SURCHARGE))
      },
      pricing () {
        let pricing
        if (this.setAsGross) {
          pricing = priceCalculator.gross(this.priceSetByCinema, this.taxable, this.taxes, this.order?.taxExempt, this.displayRedemptionTotal)
        } else {
          pricing = priceCalculator.net(this.priceSetByCinema, this.taxable, this.taxes, this.order?.taxExempt, this.redemptionTotal)
        }

        if (this.roundPrices) {
          pricing = priceCalculator.round(pricing.subtotal, pricing.taxAmount, pricing.total, this.taxable, this.taxes, this.order?.taxExempt)
        }

        return pricing
      },
      subtotal () {
        if (this.paid) {
          return this.computedPaidSubtotal
        }

        return currency(this.pricing.subtotal).add(this.bookingFeeAmount).value
      },
      total () {
        if (this.paid) {
          return this.computedPaidTotal
        }

        return currency(this.pricing.total).add(this.bookingFeeTotal).value
      },
      canRemove () {
        if (this.paidAmount > 0 && this.context?.currentRegisterClient?.registerStation?.paymentConnectorRequirePaymentByOrderItem) {
          return 'Cannot remove order item, complete payment first'
        } else {
          return true
        }
      },
      reconciliationDisplayTotal () {
        return currency(this.data.price).add(this.data.taxAmount).subtract(this.discountAmount).subtract(this.voucherAmount).value
      },
      includeBookingFeeInPrice () {
        return this.orderItemBookingFee?.bookingFeeType?.includeInPrice || this.localBookingFeeType?.includeInPrice || false
      },
      bookingFeeDisplayPrice () {
        if (!_.isNil(this.orderItemBookingFee)) {
          return this.orderItemBookingFee?.displayPrice
        } else if (!_.isNil(this.localBookingFeeType)) {
          return this.setAsGross ? this.bookingFeeTotal : this.bookingFeeAmount
        } else {
          return 0
        }
      },
      displayPrice () {
        let displayPrice = this.priceSetByCinema

        if (this.includeBookingFeeInPrice) {
          displayPrice = currency(displayPrice).add(this.bookingFeeDisplayPrice).value
        }

        return displayPrice
      },
      displayTotal () {
        return currency(this.displayPrice).subtract(this.displayRedemptionTotal).value
      },
      bookingFeePricing () {
        if (_.isNil(this.localBookingFeeType)) {
          return {
            subtotal: 0,
            taxAmount: 0,
            total: 0,
            taxes: []
          }
        }

        const bookingFeeTaxes = _.filter(this.localBookingFeeType.taxes.data, tax => _.includes(tax.data.siteIds, this.configuration.currentSite.id)) || []
        let pricing

        const bookingFeeAmount = bookingFeePriceComputer.computeForBookingFeeType(this.localBookingFeeType, this)

        if (this.setAsGross) {
          pricing = priceCalculator.gross(bookingFeeAmount, this.localBookingFeeType.taxable, bookingFeeTaxes, this.order?.taxExempt, 0)
        } else {
          pricing = priceCalculator.net(bookingFeeAmount, this.localBookingFeeType.taxable, bookingFeeTaxes, this.order?.taxExempt, 0)
        }

        if (this.roundPrices) {
          pricing = priceCalculator.round(pricing.subtotal, pricing.taxAmount, pricing.total, this.localBookingFeeType.taxable, bookingFeeTaxes, this.order?.taxExempt)
        }

        return pricing
      },
      bookingFeeAmount () {
        return this.bookingFeePricing.subtotal
      },
      bookingFeeTaxAmount () {
        return this.bookingFeePricing.taxAmount
      },
      bookingFeeTotal () {
        return this.bookingFeePricing.total
      },
      virtual () {
        return _.includes(ORDER_ITEM_TYPES.VIRTUAL, this.type)
      },
      item () {
        return this.itemOrderItem?.item
      },
      fastPrepEligable () {
        if (this.order.deliverFastPrepItems) return false

        if (this.item.preparationTimeMinutes <= fastPrepMinutes) return true
      },
      effectiveDeliveryLocationLabel () {
        if (this.type !== ORDER_ITEM_TYPES.ITEM) return null

        return this.deliveryLocation || this.defaultDeliveryLocation
      },
      deliveryTypeName () {
        if (this.customDeliveryType) return this.customDeliveryType.name

        const deliveryType = this.deliveryTypeForPlacing || this.defaultDeliveryType

        if (deliveryType === DELIVERY_TYPE_OPTIONS.COLLECT_FROM_REGISTER && this.itemSourceLocationId) {
          const itemSourceLocation = _.find(this.configuration.locations.data, location => location.id === this.itemSourceLocationId)
          return itemSourceLocation ? `Collect from ${itemSourceLocation.name}` : deliveryType
        }

        return deliveryType
      },
      customDeliveryType () {
        const deliveryType = this.deliveryTypeForPlacing || this.defaultDeliveryType

        if (!Number.isInteger(Number.parseInt(deliveryType))) return null

        return _.find(this.deliveryTypeOptions, deliveryTypeOption => deliveryTypeOption.id === Number.parseInt(deliveryType))
      },
      deliveryTypeForPlacing () {
        if (_.isNil(this.order)) return null

        if (this.virtual) return DELIVERY_TYPE_OPTIONS.VIRTUAL

        if (this.deliveryType) return this.deliveryType

        if (_.first(this.deliveryTypeOptions)?.value === DELIVERY_TYPE_OPTIONS.COLLECT_FROM_REGISTER) return DELIVERY_TYPE_OPTIONS.COLLECT_FROM_REGISTER

        if (_.first(this.deliveryTypeOptions)?.value === DELIVERY_TYPE_OPTIONS.DIRECT_TO_CUSTOMER && !this.order.deliverReadyToServeItems) return DELIVERY_TYPE_OPTIONS.DIRECT_TO_CUSTOMER

        if (_.first(this.deliveryTypeOptions)?.value === DELIVERY_TYPE_OPTIONS.REGISTER && !this.order.deliverFastPrepItems) return DELIVERY_TYPE_OPTIONS.REGISTER

        return this.order.deliveryType
      },
      defaultDeliveryType () {
        if (_.isNil(this.type)) return null

        if (this.virtual) return DELIVERY_TYPE_OPTIONS.VIRTUAL

        if (this.type !== ORDER_ITEM_TYPES.ITEM) throw new Error(`Unhandled order item type ${this.type}`)

        if (_.isNil(this.item)) return null

        return _.first(_.filter(this.deliveryTypeOptions, deliveryTypeOption => { return deliveryTypeOption.value !== DELIVERY_TYPE_OPTIONS.VIRTUAL }))?.value || null
      },
      defaultDeliveryLocation () {
        if (_.isNil(this.type)) return null

        if (this.type !== ORDER_ITEM_TYPES.ITEM) throw new Error(`Unhandled order item type ${this.type}`)

        if (!this.item) return null

        if (!_.includes(DELIVERY_TYPE_OPTIONS.DELIVERY_LOCATION_REQUIRED, this.deliveryTypeForPlacing)) return null

        if (!_.isNil(this.deliveryType) && this.deliveryType !== this.order.deliveryType) return null

        return this.order.deliveryLocation
      },
      defaultDeliveryPositions () {
        if (_.isNil(this.type)) return []

        if (this.type !== ORDER_ITEM_TYPES.ITEM) throw new Error(`Unhandled order item type ${this.type}`)

        if (!this.item) return []

        if (!_.includes(DELIVERY_TYPE_OPTIONS.DELIVERY_LOCATION_REQUIRED, this.deliveryTypeForPlacing)) return []

        if (!_.isNil(this.deliveryLocation) && this.deliveryLocation !== this.order.deliveryLocation) return []

        return this.order.deliveryPositions
      },
      deliveryLocationLabel () {
        if (this.deliveryTypeForPlacing === DELIVERY_TYPE_OPTIONS.TABLE) {
          return 'Table'
        } else if (this.deliveryTypeForPlacing === DELIVERY_TYPE_OPTIONS.THEATER_SEAT) {
          return 'Screen'
        } else {
          return 'Location'
        }
      },
      deliveryPositionLabel () {
        if (this.deliveryTypeForPlacing === DELIVERY_TYPE_OPTIONS.TABLE) {
          return 'Position'
        } else if (this.deliveryTypeForPlacing === DELIVERY_TYPE_OPTIONS.THEATER_SEAT) {
          return 'Seat'
        } else {
          return 'Position'
        }
      },
      deliveryTypeOptions () {
        return orderItemDeliveryOptionsComputer.deliveryTypeOptions(this)
      },
      deliveryLocationOptions () {
        return orderItemDeliveryOptionsComputer.deliveryLocationOptions(this)
      },
      deliveryPositionOptions () {
        return orderItemDeliveryOptionsComputer.deliveryPositionOptions(this)
      },
      displayDeliveryPositions () {
        return this.deliveryPositions?.length ? this.deliveryPositions : this.defaultDeliveryPositions
      },
      defaultHasDueTime () {
        if (this.virtual) {
          return false
        } else if (this.type === ORDER_ITEM_TYPES.ITEM) {
          return this.order.hasDueTime
        } else {
          throw new Error('Unhandled order item type: ' + this.type)
        }
      },
      defaultDueTime () {
        if (this.virtual) {
          return null
        } else if (this.type === ORDER_ITEM_TYPES.ITEM) {
          return this.order.dueTime
        } else {
          throw new Error('Unhandled order item type: ' + this.type)
        }
      },
      formattedDueTime () {
        return this.order.formatDueTime(this.dueTime)
      },
      collectNameForOrder () {
        return (!this.order.user || this.order.user.isLocal) &&
          (
            this.deliveryType === DELIVERY_TYPE_OPTIONS.COUNTER_PICKUP ||
            this.deliveryType === DELIVERY_TYPE_OPTIONS.EXPRESS_PICKUP
          )
      },
      collectPagerNumber () {
        return this.configuration.currentSite.hasCounterPager && this.deliveryType === DELIVERY_TYPE_OPTIONS.COUNTER_PICKUP && !(this.hasDueTime || this.defaultHasDueTime)
      },
      cardUids () {
        if (this.issuedVoucherPackage?.issuedVoucherPackages?.data?.length) {
          return _.map(this.issuedVoucherPackage.issuedVoucherPackages.data, issuedVoucherPackage => issuedVoucherPackage.card?.uid)
        }

        return [this.voucherPurchase?.card?.uid || this.giftCard?.card?.uid || this.issuedVoucherPackage?.card?.uid]
      },
      requiresPrinting () {
        return !_.includes([DELIVERY_TYPE_OPTIONS.COLLECT_FROM_REGISTER, DELIVERY_TYPE_OPTIONS.DIRECT_TO_CUSTOMER], this.deliveryTypeForPlacing)
      },
      requiresPrintingNow () {
        return this.requiresPrinting && !this.hasDueTime
      },
      itemColocatedWithRegister () {
        return this.itemOrderItem?.item?.hasLocation(this.currentLocation)
      },
      currentRegisterStation () {
        return this.order?.currentRegisterStation
      },
      currentRegisterType () {
        return this.currentRegisterStation?.registerType
      },
      currentLocation () {
        return this.currentRegisterType?.location
      },
      consumerRefundable () {
        if (!this.configuration.currentSite.allowConsumerRefunds) {
          return { isRefundable: false, reason: this.i18n.t('order.cannotRefundReasons.salesAreNotRefundable') }
        }

        if (this.type === ORDER_ITEM_TYPES.BOOKING_FEE && !this.bookingFeeType.refundable) {
          return { isRefundable: false, reason: this.i18n.t('order.cannotRefundReasons.bookingFeeNotRefundable') }
        }

        if (this.state !== 'placed' && this.state !== 'paid') {
          return { isRefundable: false, reason: this.i18n.t('order.cannotRefundReasons.notRefundableState') }
        }

        if (!_.includes(ORDER_ITEM_TYPES.CONSUMER_REFUNDABLE, this.type)) {
          return { isRefundable: false, reason: this.i18n.t('order.cannotRefundReasons.itemTypeNotRefundable', { itemType: this.type }) }
        }

        if (this.collectedAt) {
          return { isRefundable: false, reason: this.i18n.t('order.cannotRefundReasons.collected') }
        }

        if (this.type === ORDER_ITEM_TYPES.TICKET) {
          const showing = this.ticket.showing
          const refundThreashold = addToDate(new Date(), { minutes: this.configuration.currentSite.consumerRefundCutoffMinutes })

          if (!showing.allowConsumerRefunds) {
            return { isRefundable: false, reason: this.i18n.t('order.cannotRefundReasons.salesFinalForShowing') }
          }

          if (showing.time < refundThreashold) {
            return { isRefundable: false, reason: this.i18n.t('order.cannotRefundReasons.refundCutoff') }
          }
        }

        return { isRefundable: true, reason: null }
      },
      itemDeliveryClass () {
        if (this.type !== ORDER_ITEM_TYPES.ITEM || !this.item) return null

        if (this.item.requiresPreparation && this.item.preparationTimeMinutes > fastPrepMinutes) {
          return 'preparation-required'
        } else if (this.item.requiresPreparation) {
          return 'fast-prep'
        } else {
          return 'ready-to-serve'
        }
      },
      inventoryStrategy () {
        return this.configuration.currentSite?.activeInventoryStrategy
      },
      info () {
        if (this.type === ORDER_ITEM_TYPES.TICKET && this.ticket.ticketType.isCeaCard && this.order.hasUnappliedCeaVoucher) {
          return 'Add a second ticket for Cea Voucher to apply.'
        }
      },
      errors () {
        if (this.type === ORDER_ITEM_TYPES.TICKET && this.ticket.showing.reservedSeating && !this.ticket.seatName) {
          return 'Must select seat'
        }

        if (this.type === ORDER_ITEM_TYPES.ACTIVITY_TICKET && !this.activityTicket.activityBooking?.canBePlaced) {
          return this.activityTicket.activityBooking?.incompleteErrors.join('\n') || `No activity booking found for ID ${this.activityTicket.activityBookingId}`
        }

        if (this.type === ORDER_ITEM_TYPES.ITEM && this.item && !this.defaultDeliveryType) {
          return `There are no delivery types available for this ${this.itemDeliveryClass
            } item coming from source location ${this.item?.primaryLocationForRegisterStation(this.currentRegisterStation)?.name || 'unknown'
            }`
        }

        if (
          this.type === ORDER_ITEM_TYPES.ITEM && (
            this.itemOrderItem.itemVariation.salesDisabled ||
            _(this.itemOrderItem.itemModifiers.data).some('modifier.salesDisabled')
          )
        ) {
          return 'Item variation and/or modifiers are unavailable for sale.'
        }

        const restrictOutOfStock = this.inventoryStrategy?.inventoryRestrictionType === SALES_ITEM_RESTRICTION_TYPES.RESTRICT

        if (this.type === ORDER_ITEM_TYPES.ITEM && restrictOutOfStock) {
          const itemVariationIsOutOfStock = isOutOfStock(this.itemOrderItem.itemVariation, { itemOrderItem: this.itemOrderItem, orderItem: this })

          const modifiersAreOutOfStock = _(this.itemOrderItem.itemModifiers.data)
            .some(itemModifier => isOutOfStock(itemModifier.modifier, { item: this.itemOrderItem.item, itemOrderItem: this.itemOrderItem, orderItem: this }))

          if (itemVariationIsOutOfStock || modifiersAreOutOfStock) {
            return 'Item variation and/or modifiers are out of stock.'
          }
        }

        return null
      },
      isDelivered () {
        return !!this.deliveredAt
      },
      isHeld () {
        return this.hasDueTime && !this.dueTime
      },
      isUndelivered () {
        return !this.deliveredAt
      },
      isPrepared () {
        return !!this.preparedAt
      },
      isReadyForDelivery () {
        return !!this.preparedAt && !this.deliveredAt
      },
      isUnprepared () {
        return !this.preparedAt
      },
      isDraft () {
        return this.state === ORDER_ITEM_STATES.DRAFT
      },
      isPlaced () {
        return this.state === ORDER_ITEM_STATES.PLACED
      },
      isVoided () {
        return this.state === ORDER_ITEM_STATES.VOIDED
      },
      isUnpaid () {
        return this.isPlaced || this.isDraft
      },
      isRemovable () {
        return this.isUnpaid && !this.isVoided
      },
      siteDueTime () {
        if (!this.dueTime) return null

        return this.dueTime.inTimeZone(this.configuration.currentSite.timeZone)
      },
      siteDeliveredTime () {
        if (!this.deliveredAt) return null

        return this.deliveredAt.inTimeZone(this.configuration.currentSite.timeZone)
      },
      localShowing () {
        return this.ticket?.showing || this.order?.nextShowing || this.order?.showing
      },
      localActivityTicket () {
        return this.activityTicket || this.order?.nextActivityTicket || this.order?.activityTicket
      },
      isGiftCard () {
        return this.type === ORDER_ITEM_TYPES.GIFT_CARD
      },
      isTicket () {
        return this.type === ORDER_ITEM_TYPES.TICKET
      },
      hasTax () {
        return this.taxes.length > 0
      }
    }

    this.watchers = [
      [
        function () { return [this.effectiveDeliveryLocationLabel, this.order?.settingData, this.subtotal, this.configuration.bookingFeeTypes?.data, this.effectiveUser?.id] },
        async function () {
          if (this.type !== ORDER_ITEM_TYPES.TICKET && this.type !== ORDER_ITEM_TYPES.ITEM) return
          if (!this.draft) return
          if (this.order?.settingData) return

          if (!_.isNil(this.orderItemBookingFee) && !this.orderItemBookingFee.draft) return

          const computer = this.type === ORDER_ITEM_TYPES.TICKET ? ticketBookingFeeComputer : itemBookingFeeComputer
          const computedBookingFeeType = computer.compute(this)
          if (_.isNil(computedBookingFeeType) && !_.isNil(this.orderItemBookingFee)) {
            this.orderItems.remove(this.orderItemBookingFee)
            this.orderItemBookingFee = null
          } else if (!_.isNil(computedBookingFeeType) && !_.isNil(this.orderItemBookingFee) && computedBookingFeeType.id !== this.orderItemBookingFee.bookingFeeTypeId) {
            this.orderItemBookingFee.bookingFeeTypeId = computedBookingFeeType.id
            this.orderItemBookingFee.bookingFeeType = computedBookingFeeType
          } else if (_.isNil(this.orderItemBookingFee) && this.localBookingFeeType?.id !== computedBookingFeeType?.id) {
            this.localBookingFeeType = computedBookingFeeType
          }
        },
        { immediate: true }
      ],
      [
        function () { return this.deliveryTypeForPlacing },
        function () {
          if (_.isNil(this.type) || this.virtual) return

          if (!this.draft) return

          if (_.isNil(this.deliveryLocation)) return

          if (!_.includes(DELIVERY_TYPE_OPTIONS.DELIVERY_LOCATION_REQUIRED, this.deliveryTypeForPlacing)) {
            this.deliveryLocation = null
          } else {
            const deliveryLocation = _.find(this.configuration.deliveryLocations.data, { name: this.deliveryLocation })
            if (!_.isNil(deliveryLocation) && this.deliveryTypeForPlacing !== deliveryLocation.type) {
              this.deliveryLocation = null
            }
          }
        }
      ],
      [
        function () { return this.deliveryLocation },
        function () {
          if (_.isNil(this.type) || this.virtual) return

          if (!this.draft) return

          if (_.isEmpty(this.deliveryPositions)) return

          if (_.isNil(this.deliveryLocation)) {
            this.deliveryPositions = []
            return
          }

          const deliveryLocation = _.find(this.configuration.deliveryLocations.data, { name: this.deliveryLocation })
          if (_.isNil(deliveryLocation)) {
            this.deliveryPositions = []
            return
          }

          const hasInvalidDeliveryPositions = _.some(this.deliveryPositions, position => !_.includes(deliveryLocation.positions, position))
          if (hasInvalidDeliveryPositions) {
            this.deliveryPositions = []
          }
        }
      ]
    ]

    this.paginate = true
  }

  resetDeliveryPositions () {
    if (_.isEmpty(this.deliveryPositions)) return

    this.deliveryPositions = []
  }

  place () {
    if (this.hasDueTime === null) {
      this.hasDueTime = this.defaultHasDueTime
    }
    if (this.dueTime === null) {
      this.dueTime = this.defaultDueTime
    }
    if (this.deliveryType === null) {
      this.deliveryType = this.deliveryTypeForPlacing
    }
    if (this.collectNameForOrder && this.nameForOrder === null) {
      this.nameForOrder = this.order.nameForOrder
    }
    if (this.collectPagerNumber && this.pagerNumber === null) {
      this.pagerNumber = this.order.pagerNumber
    }
    if (this.customDeliveryType && this.customDeliveryType.hasDetails) {
      this.deliveryDetails = this.order.deliveryDetails
    }
    if (_.includes(DELIVERY_TYPE_OPTIONS.DELIVERY_LOCATION_REQUIRED, this.deliveryType)) {
      this.deliveryLocation ||= this.order.deliveryLocation
      this.deliveryPositions = !_.isEmpty(this.deliveryPositions) ? this.deliveryPositions : this.order.deliveryPositions
    }
  }

  taxFromGross (price) {
    let taxAmount = 0
    _.each(this.taxes, tax => {
      taxAmount = currency(taxAmount).add(tax.taxFromGross(price, this.totalTaxPercentThousandths)).value
    })

    return taxAmount
  }

  taxFromNet (price) {
    let taxAmount = 0
    _.each(this.taxes, tax => {
      taxAmount = currency(taxAmount).add(tax.taxFromNet(price)).value
    })

    return taxAmount
  }

  // Custom sort
  customSortValue (orderItem) {
    const sortTypeOrder = ['membership', 'member', ORDER_ITEM_TYPES.DAY_PASS, ORDER_ITEM_TYPES.TICKET]

    // First by type
    let typeOrder = sortTypeOrder.indexOf(orderItem.type)
    if (typeOrder === -1) {
      typeOrder = Number.MAX_SAFE_INTEGER
    }

    // Then member tickets before guests
    const memberTickets = (orderItem.type === ORDER_ITEM_TYPES.TICKET && orderItem.ticket?.user) ? -1 : 0

    // Then by price if member
    const memberPrice = (orderItem.type === 'member') ? -orderItem.price : 0

    // Then by remote creation order
    const remoteOrder = (orderItem.id > 0) ? orderItem.id : Number.MAX_SAFE_INTEGER

    // Then by local creation order
    const localOrder = (orderItem.id < 0) ? -orderItem.id : Number.MAX_SAFE_INTEGER

    return [typeOrder, memberTickets, memberPrice, remoteOrder, localOrder]
  }

  /**
   * Returns an object with completed order information commonly requested for
   * analytics purposes. This may be embedded in the DOM as a JSON string, or
   * used to map to a data layer or as a payload for an analytics event.
   */
  get analyticsData () {
    return {
      name: this.description,
      type: this.type,
      total: this.total
    }
  }

  get nonRefundableReason () {
    if (isConsumer && !this.consumerRefundable.isRefundable) {
      return this.consumerRefundable.reason
    }

    if (!isConsumer && this.collectedAt && (this.state === 'placed' || this.state === 'paid')) {
      return 'Collected items cannot be refunded'
    }

    if (!isConsumer && this.collectedAt && this.state === 'voided') {
      return 'Voided items cannot be refunded'
    }

    return null
  }

  get splitRatio () {
    return this.splitPaymentDetails.splitRatio
  }

  set splitRatio (splitRatio) {
    this.splitPaymentDetails.splitRatio = splitRatio
  }

  get payAmount () {
    return this.splitPaymentDetails.payAmount
  }

  set payAmount (payAmount) {
    this.splitPaymentDetails.payAmount = payAmount
  }

  get paidAmount () {
    return this.splitPaymentDetails.paidAmount
  }

  set paidAmount (paidAmount) {
    this.splitPaymentDetails.paidAmount = paidAmount
  }

  get user () {
    return this.order?.user
  }

  get effectiveUser () {
    if (this.type === ORDER_ITEM_TYPES.TICKET && !_.isNil(this.ticket?.user)) {
      return this.ticket.user
    }

    return this.order?.user
  }

  get showing () {
    return this.order?.showing
  }

  get orderItems () {
    return this.parent
  }

  get title () {
    if (this.type === ORDER_ITEM_TYPES.ITEM) {
      return this.item.name
    } else if (this.type === ORDER_ITEM_TYPES.VOUCHER_PURCHASE || this.type === ORDER_ITEM_TYPES.VOUCHER_SERVICE_ISSUE) {
      return this.voucherPurchase.voucherType.name
    } else if (this.type === ORDER_ITEM_TYPES.VOUCHER_PACKAGE_PURCHASE) {
      return this.issuedVoucherPackage.voucherPackage.name
    }

    return _.capitalize(this.type)
  }

  get checkoutDescription () {
    if (this.description) {
      return this.description
    } else if (this.type === ORDER_ITEM_TYPES.ITEM) {
      const formattedPrice = this.hasCustomPrice ? null : this.i18n.n(this.itemOrderItem.itemVariation.price, 'currency')
      return _.compact([this.itemOrderItem.item.name, this.itemOrderItem.itemVariation.name, formattedPrice, _.map(this.itemOrderItem.itemModifiers.data, 'description').join(', ')]).join(' - ')
    } else if (this.type === ORDER_ITEM_TYPES.TICKET) {
      return _.compact([this.ticket.ticketNumber, this.ticket.showing.movie.name, this.ticket.showing.formattedTime(), this.ticket.showing.screen.name, this.ticket.seatName, this.ticket.price.format]).join(' - ')
    } else if (this.type === ORDER_ITEM_TYPES.MEMBERSHIP_INTERVAL) {
      return 'Membership'
    } else if (this.type === ORDER_ITEM_TYPES.VOUCHER_PURCHASE) {
      return 'Purchase: ' + this.voucherPurchase.voucherType.details
    } else if (this.type === ORDER_ITEM_TYPES.VOUCHER_PACKAGE_PURCHASE) {
      if (this.issuedVoucherPackage.issuedVoucherPackages.data.length) {
        return 'Purchases: ' + _.map(this.issuedVoucherPackage.issuedVoucherPackages.data, issuedVoucherPackage => issuedVoucherPackage.voucherType.details).join(', ')
      } else {
        return 'Purchase: ' + this.issuedVoucherPackage.voucherType.details
      }
    } else if (this.type === ORDER_ITEM_TYPES.VOUCHER_SERVICE_ISSUE) {
      return 'Service Issue: ' + this.voucherPurchase.voucherType.details
    } else if (this.type === ORDER_ITEM_TYPES.GIFT_CARD) {
      return 'Purchase Gift Card for ' + this.giftCard.initialValue.format
    } else if (this.type === ORDER_ITEM_TYPES.DAY_PASS) {
      return 'Day Pass Purchase'
    } else if (this.type === ORDER_ITEM_TYPES.MISC_ITEM) {
      return 'Miscellaneous'
    } else if (this.type === ORDER_ITEM_TYPES.ACCOUNTING_ADJUSTMENT) {
      return 'Accounting Adjustment'
    } else if (this.type === ORDER_ITEM_TYPES.DONATION) {
      return 'Donation'
    } else if (this.type === ORDER_ITEM_TYPES.USER_CREDIT) {
      return 'Purchase user credit'
    } else if (this.type === ORDER_ITEM_TYPES.ADJUST_POINTS) {
      return 'Adjust user points'
    } else if (this.type === ORDER_ITEM_TYPES.TIP) {
      return 'Tip'
    } else if (this.type === ORDER_ITEM_TYPES.BOOKING_FEE) {
      return this.bookingFeeType?.name
    } else if (this.type === ORDER_ITEM_TYPES.SURCHARGE) {
      return 'Card Surcharge'
    } else {
      return ''
    }
  }

  get finalDescription () {
    if (this.type === ORDER_ITEM_TYPES.DONATION && (!this.details || !this.details[0])) return 'General'

    return this.description || (this.details && this.details[0] && this.details[0].title)
  }

  get sortOrder () {
    return this.options.sortOrder || 'custom'
  }

  get preparationPrintsAt () {
    if (!this.hasDueTime || !this.dueTime) return null

    return new IsoDateTime(addToDate(this.dueTime, { minutes: -this.item.preparationTimeMinutes - this.configuration.currentSite.packTimeMinutes }))
  }

  get genericEquivalencyString () {
    if (this.type === ORDER_ITEM_TYPES.VOUCHER_PURCHASE) {
      return this.voucherEquivalencyString
    } else if (this[`${_.camelCase(this.type)}EquivalencyString`]) {
      return this[`${_.camelCase(this.type)}EquivalencyString`]
    } else {
      return this.type
    }
  }

  get ticketEquivalencyString () {
    const strings = [
      this.state,
      this.ticket.ticketType.id,
      this.voucher?.id,
      this.voucherType?.id,
      this.discount?.id,
      this.itemPackageId
    ]
    return strings.join(';')
  }

  get activityTicketEquivalencyString () {
    const strings = [
      this.state,
      this.activityTicket.ticketType.id,
      this.voucher?.id,
      this.voucherType?.id,
      this.discount?.id,
      this.itemPackageId
    ]
    return strings.join(';')
  }

  get itemModifiersEquivalencyString () {
    const strings = [
      this.itemOrderItem.itemVariation.id,
      ...(this.itemOrderItem.itemModifierNames || _(this.itemOrderItem.itemModifiers.data).map('modifier.id').sort().join(','))
    ]
    return strings.join(';')
  }

  get effectiveDeliveryLocationModel () {
    return _.find(this.configuration?.deliveryLocations?.data, { type: this.deliveryTypeForPlacing || this.defaultDeliveryType, name: this.deliveryLocation || this.defaultDeliveryLocation })
  }

  get packageGroupIdentifier () {
    return this.itemPackageDetails?.packageGroupIdentifier
  }

  set packageGroupIdentifier (packageGroupIdentifier) {
    this.itemPackageDetails.packageGroupIdentifier = packageGroupIdentifier
  }

  get excludedItemPackageIds () {
    if (!_.isArray(this.itemPackageDetails.excludedItemPackageIds)) {
      this.itemPackageDetails.excludedItemPackageIds = []
    }
    return this.itemPackageDetails.excludedItemPackageIds
  }

  set excludedItemPackageIds (excludedItemPackageIds) {
    this.itemPackageDetails.excludedItemPackageIds = excludedItemPackageIds
  }

  get itemEquivalencyString () {
    const strings = [
      this.state,
      this.pickPrepPrintingStatus || 'none',
      this.itemOrderItem.itemVariation.id,
      ..._.map(this.itemOrderItem.itemModifiers.data, 'modifier.id').sort().join(','),
      this.voucher?.id,
      this.issuedVoucherPackage?.id,
      this.voucherType?.id,
      this.discount?.id,
      this.itemPackage?.id,
      this.note,
      this.hasCustomPrice ? this.total : null,
      this.hasDueTime !== this.order.hasDueTime ? this.hasDueTime : null,
      this.hasDueTime && this.dueTime !== this.order.dueTime ? this.dueTime : null,
      (this.deliveryType || this.defaultDeliveryType) !== this.order.deliveryType ? (this.deliveryType || this.defaultDeliveryType) : null,
      (this.deliveryLocation || this.defaultDeliveryLocation) !== this.order.deliveryLocation ? (this.deliveryLocation || this.defaultDeliveryLocation) : null,
      // TODO Handle when we are using defaultDeliveryPositions
      !_.isEqual(this.deliveryPositions.sort(), this.order.deliveryPositions?.sort() || []) ? this.deliveryPositions.sort() : null
    ]
    return strings.join(';')
  }

  get voucherEquivalencyString () {
    const strings = [
      this.state,
      this.price,
      this.voucherPurchase?.voucherType?.id,
      this.details.find(detail => detail.recipient_email)?.recipient_email,
      this.voucher?.id,
      this.voucherType?.id,
      this.discount?.id
    ]
    return strings.join(';')
  }

  get giftCardEquivalencyString () {
    const strings = [
      this.state,
      this.price,
      this.details.find(detail => detail.recipient_email)?.recipient_email,
      this.details.find(detail => detail.name)?.name,
      this.voucher?.id,
      this.voucherType?.id,
      this.discount?.id,
      !!this.giftCard?.card?.id
    ]
    return strings.join(';')
  }

  get purchaseEffectiveDate () {
    if (!this.effectiveAt) return null

    return this.configuration.currentSite.siteEffectiveDateForTime(this.effectiveAt)
  }

  get voidEffectiveDate () {
    if (!this.voidedAt) return null

    return this.configuration.currentSite.siteEffectiveDateForTime(this.voidedAt)
  }

  get effectiveDates () {
    return _([this.purchaseEffectiveDate, this.voidEffectiveDate])
      .compact()
      .uniqBy(value => value.getTime())
      .value()
  }

  get paymentDate () {
    if (!this.paidAt) return null

    return this.configuration.currentSite.siteEffectiveDateForTime(this.paidAt)
  }

  get refundDate () {
    // Has to both be paid and voided to be considered a refund. If just voided, is a void.
    if (!this.voidedAt) return null

    if (!this.paidAt) return null

    return this.configuration.currentSite.siteEffectiveDateForTime(this.voidedAt)
  }

  get paymentDates () {
    return _([this.paymentDate, this.refundDate])
      .compact()
      .uniqBy(value => value.getTime())
      .value()
  }

  get allowedInConsumerTab () {
    return !this.draft || _.includes(ORDER_ITEM_TYPES.ALLOWED_IN_CONSUMER_TAB, this.type)
  }

  get voidedPriorToEffectiveDate () {
    if (!this.purchaseEffectiveDate) return false

    if (!this.voidEffectiveDate) return false

    return this.voidEffectiveDate.getTime() < this.purchaseEffectiveDate.getTime()
  }

  // An order item is considered canceled if it is voided and was never paid
  get isCanceled () {
    return this.voidedAt && !this.paidAt
  }

  get isMeerkatMovieTicket () {
    const hasDcmEdiMovieId = this.ticket?.showing?.movie?.dcmEdiMovieId
    return this.isTicket && _.isNil(this.voucherType) && this.isDraft && hasDcmEdiMovieId
  }
}
